import React from 'react';
import StateTag from './StateTag';
import StatusDot from './StatusDot';
import clsx from 'clsx';
import { IExperiment } from '../../apiHooks/experimentApi.types';

interface IProps {
    style: React.CSSProperties;
    experiment: IExperiment | null;
    index: number;
}

const cellStyling = 'overflow-hidden text-ellipsis whitespace-nowrap text-gray-600 content-center';

export default function Row(props: IProps) {
    const { style, experiment, index } = props;

    if (!props.experiment) {
        return (
            <div
                style={style}
                className={clsx(
                    'flex flex-row border border-gray-100 content-center hover:bg-gray-200 cursor-pointer',
                    index % 2 ? 'bg-gray-50' : 'bg-white',
                )}
            >
                <div className={clsx('flex-auto px-6 flex items-center', cellStyling)}>
                    <div className='flex-shrink-0 mr-3 md:hidden'>
                        <div className='flex-none rounded-full p-1 bg-gray-200'>
                            <div className='h-2 w-2 rounded-full bg-gray-300' />
                        </div>
                    </div>
                    <div className='space-y-2'>
                        <div className='w-32 h-4 bg-gray-300 rounded-md animate-pulse' />
                        <div className='w-64 h-4 bg-gray-300 rounded-md animate-pulse' />
                    </div>
                </div>
                <div className={clsx('hidden md:block flex-none w-32', cellStyling)}>
                    <div className='mx-auto w-14 h-4 bg-gray-300 rounded-md animate-pulse' />
                </div>
                <div className={clsx('flex-none w-28 lg:w-56 px-3', cellStyling)}>
                    <div className='mx-auto w-28 h-4 bg-gray-300 rounded-md animate-pulse' />
                </div>
                <div className={clsx('hidden md:block flex-none w-28 lg:w-56 px-3', cellStyling)}>
                    <div className='mx-auto w-28 h-4 bg-gray-300 rounded-md animate-pulse' />
                </div>
            </div>
        );
    }

    if (experiment.uuid) {
        const experimentState = experiment.result_counts.in_progress === 0 ? 'completed' : 'in_progress';

        return (
            <a
                style={style}
                href={`/experiments/${experiment.uuid}/results/${experiment.last_created_result.uuid}/`}
                className={clsx(
                    'flex flex-row border border-gray-100 content-center hover:bg-gray-200 hover:no-underline cursor-pointer',
                    index % 2 ? 'bg-gray-50' : 'bg-white',
                )}
            >
                <div className={clsx('flex-auto px-6 flex items-center', cellStyling)}>
                    <div className='flex-shrink-0 mr-3 md:hidden'>
                        <StatusDot state={experimentState} />
                    </div>
                    <p className='truncate' title={experiment.name}>
                        <span className='font-bold text-gray-900'>{experiment.name}</span>
                        <br />
                        Experiment with {experiment.result_counts.total} result
                        {experiment.result_counts.total > 1 ? 's' : ''}
                    </p>
                </div>
                <div className={clsx('hidden md:block flex-none w-32 text-center', cellStyling)}>
                    <StateTag state={experimentState} />
                </div>
                <div className={clsx('flex-none w-28 lg:w-56 px-3', cellStyling)}>
                    {formatDate(experiment.created_at)}{' '}
                    <span className='hidden lg:inline-block'>{formatTime(experiment.created_at)}</span>
                </div>
                <div className={clsx('hidden md:block flex-none w-28 lg:w-56 px-3', cellStyling)}>
                    {formatDate(experiment.finished_at)}{' '}
                    <span className='hidden lg:inline-block'>{formatTime(experiment.finished_at)}</span>
                </div>
            </a>
        );
    }

    const result = experiment.last_created_result;
    const resultState = result.state.replace('failed', 'error');
    return (
        <a
            href={`/results/${result.uuid}/`}
            style={style}
            className={clsx(
                'flex flex-row border border-gray-100 content-center hover:bg-gray-200 hover:no-underline cursor-pointer',
                index % 2 ? 'bg-gray-50' : 'bg-white',
            )}
        >
            <div className={clsx('flex-auto px-6 flex items-center', cellStyling)}>
                <div className='flex-shrink-0 mr-3 md:hidden'>
                    <StatusDot state={resultState} />
                </div>
                <p className='truncate' title={`${result.name}\n${result.app_uri}`}>
                    <span className='font-bold text-gray-900'>{result.name}</span>
                    <br />
                    {result.app_uri}
                </p>
            </div>
            <div className={clsx('hidden md:block flex-none w-32 text-center', cellStyling)}>
                <StateTag state={resultState} />
            </div>
            <div className={clsx('flex-none w-28 lg:w-56 px-3', cellStyling)}>
                {formatDate(result.created_at)}{' '}
                <span className='hidden lg:inline-block'>{formatTime(result.created_at)}</span>
            </div>
            <div className={clsx('hidden md:block flex-none w-28 lg:w-56 px-3', cellStyling)}>
                {formatDate(result.finished_at)}{' '}
                <span className='hidden lg:inline-block'>{formatTime(result.finished_at)}</span>
            </div>
        </a>
    );
}

function formatDate(isoDateString: string): string {
    if (!isoDateString) {
        return '-';
    }
    const date = new Date(isoDateString);
    return date.toLocaleDateString();
}

function formatTime(isoDateString: string): string {
    if (!isoDateString) {
        return '-';
    }
    const date = new Date(isoDateString);
    return date.toLocaleTimeString();
}
